/**
 * Anivaze v4 — client for the real voiceover endpoint (/tts, Workers AI MeloTTS).
 *
 * The killer detail: we measure each clip's ACTUAL duration and retime the beat
 * to it, so narration and visuals stay locked together — one continuous film
 * instead of "slide… stop… slide". Falls back to browser speech per beat when a
 * clip is missing, and self-heals after narration edits.
 */

import { CLIENT_ID } from "./api";

const API_BASE: string =
  (import.meta.env.VITE_ORCHESTRATOR_BASE_URL as string | undefined) ||
  "https://learnengine-orchestrator-api.obedpaul-p.workers.dev";
const USER_ID: string = CLIENT_ID;

export interface BeatAudio {
  /** Object URL of the decoded clip. */
  url: string;
  /** Measured clip duration in seconds. */
  duration: number;
  /** The narration text this clip was generated from (staleness check). */
  text: string;
}

/**
 * Narrator voices — all 12 Deepgram Aura speakers, live-verified on our /tts.
 * Curated labels describe the read, not just the name, so picking is easy.
 */
export const VOICES: Array<{ id: string; label: string }> = [
  { id: "asteria", label: "Asteria — warm, clear (default)" },
  { id: "luna", label: "Luna — soft, gentle" },
  { id: "stella", label: "Stella — bright, upbeat" },
  { id: "athena", label: "Athena — calm British" },
  { id: "hera", label: "Hera — confident, mature" },
  { id: "orion", label: "Orion — deep, steady" },
  { id: "arcas", label: "Arcas — friendly, casual" },
  { id: "perseus", label: "Perseus — energetic" },
  { id: "angus", label: "Angus — warm Irish" },
  { id: "orpheus", label: "Orpheus — smooth storyteller" },
  { id: "helios", label: "Helios — crisp British" },
  { id: "zeus", label: "Zeus — authoritative" },
];

/** Narration languages (server whitelist mirror): Aura narrates EN; MeloTTS the rest. */
export const LANGS: Array<{ id: string; label: string }> = [
  { id: "en", label: "🌐 English" },
  { id: "es", label: "🌐 Español" },
  { id: "fr", label: "🌐 Français" },
  { id: "zh", label: "🌐 中文" },
  { id: "ja", label: "🌐 日本語" },
  { id: "ko", label: "🌐 한국어" },
];

/** Fetch one narration clip and measure its real duration. */
export async function fetchTTS(text: string, signal?: AbortSignal, voice?: string, lang?: string): Promise<BeatAudio> {
  const res = await fetch(`${API_BASE}/tts`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-user-id": USER_ID },
    body: JSON.stringify({ text, ...(voice ? { voice } : {}), ...(lang && lang !== "en" ? { lang } : {}) }),
    signal,
  });
  if (!res.ok) throw new Error(`tts failed: ${res.status}`);
  const blob = await res.blob();
  if (blob.size < 512) throw new Error("tts returned no audio");
  const url = URL.createObjectURL(blob);
  try {
    const duration = await new Promise<number>((resolve, reject) => {
      const probe = new Audio();
      probe.preload = "metadata";
      probe.onloadedmetadata = () => resolve(probe.duration);
      probe.onerror = () => reject(new Error("audio decode failed"));
      probe.src = url;
    });
    if (!Number.isFinite(duration) || duration <= 0) throw new Error("bad audio duration");
    return { url, duration, text };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/**
 * Fetch clips for every beat in parallel. Resolves with whatever succeeded
 * (beats without a clip fall back to browser speech); rejects only if ALL fail.
 */
export async function prepareVoiceover(
  beats: Array<{ id: string; narration: string }>,
  voice?: string,
  lang?: string,
): Promise<Map<string, BeatAudio>> {
  const settled = await Promise.allSettled(
    beats.map((b) => fetchTTS(b.narration || " ", undefined, voice, lang)),
  );
  const map = new Map<string, BeatAudio>();
  const failed: number[] = [];
  settled.forEach((r, i) => {
    if (r.status === "fulfilled") map.set(beats[i].id, r.value);
    else failed.push(i);
  });
  // One retry pass for the failures — a stray TTS hiccup should not leave a
  // beat on the robotic browser voice (and its shakier timing).
  if (failed.length > 0) {
    const retried = await Promise.allSettled(failed.map((i) => fetchTTS(beats[i].narration || " ", undefined, voice, lang)));
    retried.forEach((r, j) => {
      if (r.status === "fulfilled") map.set(beats[failed[j]].id, r.value);
    });
  }
  if (map.size === 0) throw new Error("voiceover unavailable");
  return map;
}

/** Free the object URLs of a voiceover map. */
export function releaseVoiceover(map: Map<string, BeatAudio> | null): void {
  if (!map) return;
  for (const a of map.values()) URL.revokeObjectURL(a.url);
}
